import { keepPreviousData, QueryClient, useQuery } from '@tanstack/react-query'
import { compact } from 'lodash'
import { decodeEventLog, Hex, pad, toHex, zeroAddress } from 'viem'
import { arbitrum, polygon } from 'viem/chains'

import { ERC20_TRANSFER_TOPIC } from '~/components/txn-display/state-change/utils/deriveStateChangeResult'
import { simulateCallsOnServer } from '~/components/txn-display/state-change/utils/simulationClient'
import { WAREHOUSE_NATIVE_TOKEN } from '~/constants/addresses'
import { getChain } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_SIMULATION } from '~/constants/refetch-intervals'
import { Address, EvmAddress } from '~/utils/address'
import { captureException } from '~/utils/errors'
import { wethAbi } from '~/utils/iweth'
import { getReadPublicClient } from '~/utils/requests'
import { buildTenderlySimulationUrl } from '~/utils/tenderly'

import type {
  CustomLog,
  UseSimulateCallsArgs,
} from '~/components/txn-display/state-change/types'
import type {
  SimulatedCallTraceItem,
  SimulatedLog,
} from '~/components/txn-display/state-change/utils/simulationClient'
import type { SupportedChainId } from '~/constants/chains'
import type { BackendTransactionCall, IAddress } from '~/types'

export type SimulationError = Error & {
  isSimulationRevert?: boolean
  failingCallIndexes?: number[]
}

const isSimulationRevertError = (error: unknown): error is SimulationError =>
  error instanceof Error &&
  'isSimulationRevert' in error &&
  error.isSimulationRevert === true

const REVERT_MESSAGE_REGEX =
  /(revert|reverted|underflow|overflow|invalid opcode|panic)/i

const buildSimulationRevertError = ({
  message,
  failingCallIndexes,
}: {
  message?: string
  failingCallIndexes?: number[]
} = {}): SimulationError => {
  const error = new Error(
    `Failed to natively simulate transaction: ${message?.trim() || 'No revert reason provided.'}`,
  ) as SimulationError
  error.isSimulationRevert = true
  if (failingCallIndexes?.length) {
    error.failingCallIndexes = failingCallIndexes
  }
  return error
}

const isLikelyRevertMessage = (message: string) =>
  REVERT_MESSAGE_REGEX.test(message)

// Batch sub-calls surface in the call trace at depth-2 [0, N] from the vault;
// single-call transactions go through execute() with no wrapping index.
export const findFailingCallIndexesFromTrace = ({
  callTrace,
  smartAccountAddress,
  callCount,
}: {
  callTrace: SimulatedCallTraceItem[]
  smartAccountAddress: IAddress
  callCount: number
}): number[] => {
  if (callCount === 0) return []
  if (callCount === 1) return [0]
  return callTrace
    .filter(
      (item) =>
        item.callType === 'CALL' &&
        Address.equal(item.from, smartAccountAddress, 'evm') &&
        item.traceAddress?.length === 2 &&
        item.traceAddress[0] === 0 &&
        !!item.error,
    )
    .map((item) => {
      const [, callIndex] = item.traceAddress!
      return callIndex
    })
}

const getSimulateCallsQueryKey = ({
  smartAccountAddress,
  chainId,
  calls,
}: UseSimulateCallsArgs) => [
  QUERY_KEYS.SIMULATE_CALLS,
  smartAccountAddress,
  chainId,
  calls?.map((call) => ({
    to: call.to,
    data: call.data,
    value: call.value?.toString(),
  })),
  chainId ? (getChain(chainId)?.alchemyPrefix ?? null) : null,
]

export const simulateCallsQueryFn = async ({
  smartAccountAddress,
  chainId,
  calls,
  transactionId,
}: {
  smartAccountAddress: IAddress
  chainId: SupportedChainId
  calls: BackendTransactionCall[]
  transactionId?: string
}) => {
  // Arbitrum and Polygon require Tenderly simulation due to RPC limitations with simulateCalls
  if (chainId === arbitrum.id || chainId === polygon.id) {
    return getSimulationLogsFromTenderly({
      smartAccountAddress,
      chainId,
      calls,
      transactionId,
    })
  }

  try {
    return await getSimulationLogs({
      smartAccountAddress,
      chainId,
      calls,
    })
  } catch (error) {
    // Tenderly doesn't support this chain, so skip the re-validation and
    // fallback below and surface the native result directly.
    if (!getChain(chainId)?.supportsTenderly) {
      if (!isSimulationRevertError(error)) {
        captureException(error, {
          extra: { smartAccountAddress, chainId, calls },
        })
      }
      throw error
    }
    if (isSimulationRevertError(error)) {
      // The native path simulates the raw target calls directly from the
      // SmartVault address instead of through SmartVault.execute() from the
      // EntryPoint, so calls that depend on the real execution context (e.g. a
      // WETH unwrap, which sends ETH back to the vault) can report a false
      // revert. Re-validate with the execution-shaped Tenderly simulation
      // before surfacing the revert.
      try {
        return await getSimulationLogsFromTenderly({
          smartAccountAddress,
          chainId,
          calls,
          transactionId,
        })
      } catch (executionShapedError) {
        // A genuine revert in the execution-shaped path is real, user-actionable
        // signal — surface it. Any other Tenderly failure means we couldn't
        // disprove the native revert, so fall back to surfacing that.
        if (isSimulationRevertError(executionShapedError))
          throw executionShapedError
        throw error
      }
    }
    captureException(error, {
      extra: { smartAccountAddress, chainId, calls },
    })
    return getSimulationLogsFromTenderly({
      smartAccountAddress,
      chainId,
      calls,
      transactionId,
    })
  }
}

export const useSimulateCalls = ({
  smartAccountAddress,
  chainId,
  calls,
  transactionId,
}: UseSimulateCallsArgs) => {
  // transactionId stays out of the key so a draft's cached simulation is
  // reused once the same calls are submitted.
  const queryKey = getSimulateCallsQueryKey({
    smartAccountAddress,
    chainId,
    calls,
  })

  return useQuery({
    queryKey,
    queryFn: () =>
      simulateCallsQueryFn({
        smartAccountAddress: smartAccountAddress!,
        chainId: chainId!,
        calls: calls!,
        transactionId,
      }),
    staleTime: REFETCH_SIMULATION,
    refetchInterval: REFETCH_SIMULATION,
    placeholderData: keepPreviousData,
    enabled: !!smartAccountAddress && !!chainId && !!calls && calls.length > 0,
    retry: false, // Don't retry simulation failures - they won't suddenly succeed
  })
}

/**
 * Prefetch simulation results into the React Query cache.
 * Call this before the approval dialog opens so simulation data
 * is already available when the dialog mounts.
 */
export const prefetchSimulateCalls = ({
  queryClient,
  smartAccountAddress,
  chainId,
  calls,
}: {
  queryClient: QueryClient
  smartAccountAddress: IAddress
  chainId: SupportedChainId
  calls: BackendTransactionCall[]
}) => {
  const queryKey = getSimulateCallsQueryKey({
    smartAccountAddress,
    chainId,
    calls,
  })

  queryClient.prefetchQuery({
    queryKey,
    queryFn: () =>
      simulateCallsQueryFn({
        smartAccountAddress,
        chainId,
        calls,
      }),
    staleTime: REFETCH_SIMULATION,
  })
}

type RunTenderlySimulationArgs = {
  smartAccountAddress: IAddress
  chainId: SupportedChainId
  calls: BackendTransactionCall[]
  transactionId?: string
}

export const runTenderlySimulation = async ({
  smartAccountAddress,
  chainId,
  calls,
  transactionId,
}: RunTenderlySimulationArgs): Promise<string | undefined> => {
  const result = await simulateCallsOnServer({
    smartAccountAddress,
    chainId,
    calls,
    transactionId,
    save: true,
  })

  const simulationId = result?.simulationId
  if (!simulationId) return undefined

  return buildTenderlySimulationUrl(simulationId)
}

const getSimulationLogs = async ({
  smartAccountAddress,
  chainId,
  calls,
}: {
  smartAccountAddress: IAddress
  chainId: SupportedChainId
  calls: BackendTransactionCall[]
}): Promise<CustomLog[]> => {
  const client = getReadPublicClient(chainId)

  const result = await client.simulateCalls({
    account: EvmAddress.format(smartAccountAddress),
    calls: calls.map((call) => ({
      to: EvmAddress.format(call.to),
      data: call.data as Hex,
      value: call.value,
    })),
    traceTransfers: true,
  })

  // viem's simulateCalls halts at the first reverting call, so in practice
  // this collects a single index — kept as an array to mirror the Tenderly path.
  const failingCallIndexes = result.results
    .map((item, index) => (item.status === 'failure' ? index : undefined))
    .filter((index): index is number => index !== undefined)

  if (failingCallIndexes.length > 0) {
    throw buildSimulationRevertError({ failingCallIndexes })
  }

  const logs = compact(result.results.flatMap((item) => item.logs))
  const wethTransferLogs = getWethTransferLogs(logs)

  return [...logs, ...wethTransferLogs]
}

const toCustomLog = (log: SimulatedLog): CustomLog => ({
  address: EvmAddress.format(log.address),
  topics: log.topics as [Hex, ...Hex[]],
  data: log.data as Hex,
})

const getSimulationLogsFromTenderly = async ({
  smartAccountAddress,
  chainId,
  calls,
  transactionId,
}: {
  smartAccountAddress: IAddress
  chainId: SupportedChainId
  calls: BackendTransactionCall[]
  transactionId?: string
}): Promise<CustomLog[]> => {
  const result = await simulateCallsOnServer({
    smartAccountAddress,
    chainId,
    calls,
    transactionId,
  })

  // Check if simulation failed
  if (result.errorMessage) {
    const message = result.errorMessage.trim()
    if (isLikelyRevertMessage(message)) {
      const failingCallIndexes = findFailingCallIndexesFromTrace({
        callTrace: result.callTrace,
        smartAccountAddress,
        callCount: calls.length,
      })
      throw buildSimulationRevertError({ message, failingCallIndexes })
    }
    throw new Error(message)
  }

  // Extract ETH transfers from call trace (only non-zero value transfers)
  const ethTransferLogs = compact(
    result.callTrace.map((call) => {
      if (
        call.callType === 'CALL' &&
        call.value &&
        BigInt(call.value as Hex) > 0n
      ) {
        return {
          address: WAREHOUSE_NATIVE_TOKEN as EvmAddress.Type,
          topics: [
            ERC20_TRANSFER_TOPIC,
            pad(EvmAddress.format(call.from)),
            pad(EvmAddress.format(call.to)),
          ] as [Hex, ...Hex[]],
          data: pad(call.value as Hex),
        }
      }
    }),
  )

  const normalLogs = result.logs.map(toCustomLog)
  const wethTransferLogs = getWethTransferLogs(normalLogs)

  return [...ethTransferLogs, ...normalLogs, ...wethTransferLogs]
}

/**
 * WETH Withdrawal event topic: keccak256("Withdrawal(address,uint256)")
 * Emitted when WETH is unwrapped to ETH
 */
const WETH_WITHDRAW_TOPIC =
  '0x7fcf532c15f0a6db0bd6d0e038bea71d30d808c7d98cb3bf7268a95bf5081b65' as Hex

/**
 * WETH Deposit event topic: keccak256("Deposit(address,uint256)")
 * Emitted when ETH is wrapped to WETH
 */
const WETH_DEPOSIT_TOPIC =
  '0xe1fffcc4923d04b559f4d29a8bfc6cda04eb5b0d3c460751c2402c5c5cc9109c' as Hex

/**
 * Converts WETH deposit and withdrawal events to ERC-20 Transfer events.
 * This allows WETH wrapping/unwrapping to be displayed as token transfers in the UI.
 * - Deposits are shown as transfers from address(0) to the depositor
 * - Withdrawals are shown as transfers from the withdrawer to address(0)
 */
export const getWethTransferLogs = (logs: CustomLog[]) =>
  compact(
    logs
      .filter(
        (log) =>
          log.topics[0] === WETH_WITHDRAW_TOPIC ||
          log.topics[0] === WETH_DEPOSIT_TOPIC,
      )
      .map((log) => {
        const decodedLog = decodeEventLog({
          abi: wethAbi,
          data: log.data as Hex,
          topics: log.topics as [Hex, ...Hex[]],
        })

        if (decodedLog.eventName === 'Withdrawal') {
          return {
            address: EvmAddress.format(log.address),
            topics: [
              ERC20_TRANSFER_TOPIC,
              pad(decodedLog.args.src),
              pad(zeroAddress),
            ] as [Hex, ...Hex[]],
            data: pad(toHex(decodedLog.args.wad)),
          }
        }

        if (decodedLog.eventName === 'Deposit') {
          return {
            address: EvmAddress.format(log.address),
            topics: [
              ERC20_TRANSFER_TOPIC,
              pad(zeroAddress),
              pad(decodedLog.args.dst),
            ] as [Hex, ...Hex[]],
            data: pad(toHex(decodedLog.args.wad)),
          }
        }

        return undefined
      }),
  )
